/**
 * Videos: upload once, push to any number of machines and phones.
 *
 * Upload is chunked and resumable (`api/upload.ts`) because a clip is hundreds of megabytes and a single
 * request that dies at 90% throws all of it away. Progress shows *bytes*, not a spinner: hashing a large
 * file takes seconds with nothing on the wire, and a person needs to see that this is the hashing step
 * rather than a stall.
 *
 * Distributing is a dialog, not a row button per machine: one clip goes to several machines at once,
 * and the choice — which machines, and whether every online phone on each or only some — is one
 * decision made in one place. Uniquification is a checkbox in the same dialog, and the dialog shows the
 * catalogue's capacity next to the phone count *before* the click: the service refuses a distribution
 * that needs more distinct recipes than the sources allow, and a refusal after the click is the worse
 * way to learn that.
 */

import { Download, Trash2, X } from 'lucide-react'
import { Person } from '../../ui/Person'
import { ago, exactly } from '../../lib/when'
import { Fragment, useEffect, useRef, useState, type DragEvent } from 'react'
import { useNavigate } from 'react-router-dom'

import { ApiError } from '../../api/client'
import type { CardChoice, CardPlacement, Device, Hub, Scenario, SourcesList, Video } from '../../api/types'
import { uploadSource, uploadVideo, type UploadProgress } from '../../api/upload'
import { TargetPicker, countPhones, toTargets, type Picked } from '../distribute/TargetPicker'
import { useT } from '../../i18n'
import { useApp } from '../../lib/context'
import { usePoll } from '../../lib/poll'
import { toast } from '../../lib/toast'
import { Badge, Chips, Card, Empty, Status } from '../../ui/components'
import { ConfirmDanger } from '../../ui/ConfirmDanger'
import { PhoneLink } from '../../ui/PhoneLink'
import { DirectionChip, useDirectionName, useDirections, useMyDirections } from '../marketing/directions'

const ACCEPT = 'video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm'

export function megabytes(bytes: number): string {
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`
  return `${(bytes / 1_000_000).toFixed(1)} MB`
}

interface UploadJob {
  id: number
  name: string
  progress: UploadProgress
  error?: string
}

export function VideosPanel() {
  const { api, auth, authEpoch } = useApp()
  const t = useT()
  const videos = usePoll(() => api.videos(), 4000, [api, authEpoch])
  const [uploads, setUploads] = useState<UploadJob[]>([])
  const [over, setOver] = useState(false)
  const [toDistribute, setToDistribute] = useState<Video | null>(null)
  const [toDelete, setToDelete] = useState<Video | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const nextId = useRef(1)
  //: a video belongs to one direction, named at upload (owner, 2026-10-12)
  const mine = useMyDirections()
  const dirs = useDirections()
  const nameOf = useDirectionName()
  const [uploadDir, setUploadDir] = useState('')
  const chosenDir = uploadDir || (mine.length === 1 ? mine[0]!.direction_id : '')
  const [dirFilter, setDirFilter] = useState('all')
  //: plan Stage 5 — what shoots first, or what was never posted
  const [order, setOrder] = useState<'new' | 'best' | 'never'>('new')
  const [open, setOpen] = useState<string | null>(null)

  const accept = async (files: FileList | File[] | null) => {
    if (!files) return
    if (!chosenDir) {
      toast(mine.length ? t.videos.pickDirectionFirst : t.videos.noDirectionsYou, 'bad')
      return
    }
    for (const file of Array.from(files)) {
      const id = nextId.current++
      const patch = (p: Partial<UploadJob>) => setUploads((u) => u.map((j) => (j.id === id ? { ...j, ...p } : j)))
      setUploads((u) => [...u, { id, name: file.name, progress: { stage: 'hashing', done: 0, total: file.size } }])
      try {
        const video = await uploadVideo(api, file, chosenDir, (progress) => patch({ progress }))
        if (video.status === 'error') patch({ error: video.error ?? t.videos.rejected })
        else {
          toast(t.videos.uploaded(file.name))
          setUploads((u) => u.filter((j) => j.id !== id))
        }
        videos.refresh()
      } catch (e) {
        patch({ error: e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e) })
      }
    }
  }

  const onDrop = (e: DragEvent) => {
    e.preventDefault()
    setOver(false)
    void accept(e.dataTransfer.files)
  }

  const remove = async () => {
    if (!toDelete) return
    try {
      await api.deleteVideo(toDelete.video_id)
      toast(t.videos.deleted(videoName(toDelete)))
      setToDelete(null)
      videos.refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  const all = videos.data ?? []
  const list = all.filter((v) => dirFilter === 'all' || (dirFilter === 'none' ? !v.direction_id : v.direction_id === dirFilter))
    .filter((v) => order !== 'never' || !v.results)
    .sort((a, b) => order === 'best' ? (b.results?.views ?? -1) - (a.results?.views ?? -1) : 0)
  const setDirection = async (v: Video, directionId: string) => {
    try {
      await api.setVideoDirection(v.video_id, directionId)
      videos.refresh()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
    }
  }

  return (
    <>
      <Card
        title={t.videos.title}
        sub={t.videos.sub}
        actions={
          <>
            <input
              ref={fileInput}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                void accept(e.target.files)
                e.target.value = ''
              }}
            />
            <select value={chosenDir} onChange={(e) => setUploadDir(e.target.value)} aria-label={t.videos.directionFor}
                    className={chosenDir ? '' : 'needs'}>
              <option value="">{mine.length ? t.videos.directionFor : t.videos.noDirectionsYou}</option>
              {mine.map((d) => <option key={d.direction_id} value={d.direction_id}>{nameOf(d)}</option>)}
            </select>
            <button className="primary" disabled={!chosenDir} title={chosenDir ? undefined : t.videos.pickDirectionFirst}
                    onClick={() => fileInput.current?.click()}>
              {t.videos.upload}
            </button>
          </>
        }
      >
        <div
          className={`drop${over ? ' over' : ''}`}
          onClick={() => fileInput.current?.click()}
          onDragOver={(e) => {
            e.preventDefault()
            setOver(true)
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <strong>{t.videos.dropTitle}</strong>
          <span className="dim small">{t.videos.dropBody}</span>
        </div>

        {uploads.length > 0 && (
          <div className="uploads">
            {uploads.map((u) => (
              <div className="upload-row" key={u.id}>
                <span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.name}
                </span>
                {u.error ? (
                  <>
                    <Badge kind="bad">{u.error}</Badge>
                    <button className="ghost" aria-label={t.common.dismiss} title={t.common.dismiss} onClick={() => setUploads((x) => x.filter((j) => j.id !== u.id))}><X className="ico" size={14} aria-hidden /></button>
                  </>
                ) : (
                  <>
                    <span className="dim tiny">
                      {u.progress.stage === 'hashing'
                        ? t.videos.hashing
                        : `${megabytes(u.progress.done)} / ${megabytes(u.progress.total)}`}
                    </span>
                    <span className="job-phase-bar" role="progressbar" aria-label={u.name}>
                      <span
                        className={`job-phase-fill${u.progress.stage === 'hashing' ? ' indeterminate' : ''}`}
                        style={{ width: `${u.progress.total ? Math.round((u.progress.done / u.progress.total) * 100) : 0}%` }}
                      />
                    </span>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="row" style={{ flexWrap: 'wrap', gap: 8, marginTop: 'var(--sp-2)' }}>
          <Chips value={dirFilter} onChange={setDirFilter} options={[
            { id: 'all', label: t.videos.allDirections(all.length) },
            ...dirs.list.map((d) => ({ id: d.direction_id, label: `${nameOf(d)} · ${all.filter((v) => v.direction_id === d.direction_id).length}` })),
            { id: 'none', label: t.videos.withoutDirection(all.filter((v) => !v.direction_id).length) },
          ]} />
          <Chips value={order} onChange={(x) => setOrder(x as 'new' | 'best' | 'never')} options={[
            { id: 'new', label: t.videos.orderNew },
            { id: 'best', label: t.videos.orderBest },
            { id: 'never', label: t.videos.orderNever(all.filter((v) => !v.results).length) },
          ]} />
        </div>
        <Status text={videos.error ? t.videos.readError(videos.error.message) : null} bad />
        {videos.data && list.length === 0 && <Empty>{t.videos.none}</Empty>}
        {list.length > 0 && (
          <div className="scroll-x">
            <table className="grid videos-table">
              <thead>
                <tr>
                  <th>{t.videos.file}</th>
                  <th>{t.videos.direction}</th>
                  <th>{t.videos.results}</th>
                  <th>{t.videos.state}</th>
                  <th>{t.videos.uploadedBy}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((v) => (
                  <Fragment key={v.video_id}>
                  <tr>
                    <td>
                      <VideoTitle video={v} onSaved={videos.refresh} />
                      <div className="dim tiny mono video-file">
                        {v.title ? `${v.filename} · ` : ''}
                        {v.video_id.slice(0, 8)} · {v.sha256.slice(0, 12)}
                      </div>
                    </td>
                    <td>
                      {v.direction_id && <DirectionChip direction={dirs.byId.get(v.direction_id)} />}
                      {/* a video without a direction goes nowhere until a person names one; it can be moved
                          only while it has not been posted (the service says so if it has) */}
                      {auth.mayTouch(v.uploaded_by) && mine.length > 0 && (
                        <select className={`tiny${v.direction_id ? ' quiet-select' : ' needs'}`} value=""
                                aria-label={v.direction_id ? t.videos.moveDirection : t.videos.setDirection}
                                onChange={(e) => e.target.value && void setDirection(v, e.target.value)}>
                          <option value="">{v.direction_id ? t.videos.moveDirection : t.videos.setDirection}</option>
                          {mine.filter((d) => d.direction_id !== v.direction_id).map((d) => (
                            <option key={d.direction_id} value={d.direction_id}>{nameOf(d)}</option>
                          ))}
                        </select>
                      )}
                      {!v.direction_id && !(auth.mayTouch(v.uploaded_by) && mine.length > 0) && (
                        <span className="tiny warn-text">{t.videos.noDirection}</span>
                      )}
                    </td>
                    <td className="video-results">
                      {v.results ? (
                        <>
                          <div><b>{(v.results.views ?? 0).toLocaleString()}</b> <span className="tiny dim">{t.videos.viewsWord}</span>
                            {v.results.likes != null && <> · <b>{v.results.likes.toLocaleString()}</b> <span className="tiny dim">{t.videos.likesWord}</span></>}</div>
                          <div className="tiny dim">{t.videos.postsAccounts(v.results.posts, v.results.accounts)}
                            {v.results.last_at && <> · {t.videos.lastPosted(ago(v.results.last_at))}</>}</div>
                          <span className="row" style={{ gap: 6 }}>
                            <button className="quiet tiny" aria-expanded={open === v.video_id}
                                    onClick={() => setOpen(open === v.video_id ? null : v.video_id)}>{t.videos.history(v.results.posts)}</button>
                            {v.results.best?.url && <a className="tiny" href={v.results.best.url} target="_blank" rel="noreferrer">{t.videos.bestPost(v.results.best.views.toLocaleString())}</a>}
                          </span>
                        </>
                      ) : <span className="tiny dim">{t.videos.neverPosted}</span>}
                      <div className="tiny dim">{megabytes(v.size)}</div>
                    </td>
                    <td className="col-state">
                      <Badge kind={v.status === 'ready' ? 'ok' : v.status === 'error' ? 'bad' : 'warn'} title={v.error ?? undefined}>
                        {t.videos.status[v.status]}
                      </Badge>
                    </td>
                    <td className="dim small">
                      {v.uploaded_by ? <Person who={v.uploaded_by} /> : t.common.dash}
                      {v.created_at ? <div className="tiny" title={exactly(v.created_at)}>{ago(v.created_at)}</div> : null}
                    </td>
                    <td className="video-actions">
                      <button className="primary btn-sm" disabled={v.status !== 'ready'} onClick={() => setToDistribute(v)}>
                        {t.videos.distribute}
                      </button>{' '}
                      {/* Distribute is the act this list exists for, in words; download and delete are
                          icons with their names in the tooltip (redesign stage 4) — three word buttons
                          per row were what pushed the table past a laptop's width. */}
                      {v.url && v.status === 'ready' ? (
                        <a className="icon-link" href={api.artifactUrl(v.url)} download={v.filename}
                           title={t.videos.download} aria-label={t.videos.download}>
                          <Download className="ico" size={15} aria-hidden />
                        </a>
                      ) : null}
                      <button className="ghost icon tiny danger-hover" aria-label={t.videos.delete}
                              disabled={!auth.mayTouch(v.uploaded_by)}
                              title={auth.mayTouch(v.uploaded_by) ? t.videos.delete : t.common.notYours}
                              onClick={() => setToDelete(v)}>
                        <Trash2 className="ico" size={15} aria-hidden />
                      </button>
                    </td>
                  </tr>
                  {open === v.video_id && (
                    <tr className="video-history-row"><td colSpan={6}><VideoHistory videoId={v.video_id} /></td></tr>
                  )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {toDistribute && <DistributeDialog video={toDistribute} onClose={() => setToDistribute(null)} />}
      {toDelete && (
        <ConfirmDanger
          title={t.videos.deleteTitle(videoName(toDelete))}
          confirmLabel={t.videos.delete}
          holdSeconds={2}
          onCancel={() => setToDelete(null)}
          onConfirm={() => void remove()}
        >
          <p className="dim small">{t.videos.deleteBody}</p>
        </ConfirmDanger>
      )}
    </>
  )
}

function DistributeDialog({ video, onClose }: { video: Video; onClose: () => void }) {
  const { api } = useApp()
  const t = useT()
  const navigate = useNavigate()
  const [hubs, setHubs] = useState<Hub[] | null>(null)
  const [devices, setDevices] = useState<Device[]>([])
  const [sources, setSources] = useState<SourcesList | null>(null)
  const [scenario, setScenario] = useState<Scenario | null | undefined>(undefined)
  const [publish, setPublish] = useState(true)
  const [picked, setPicked] = useState<Picked>({})
  const [uniquify, setUniquify] = useState(true)
  const [cardId, setCardId] = useState<string>('')
  const [placement, setPlacement] = useState<CardPlacement>('end')
  const [cardSeconds, setCardSeconds] = useState(2)
  const [cardBusy, setCardBusy] = useState(false)
  const cardInput = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    void Promise.all([api.hubs(), api.devices(), api.sources(), api.scenarios()]).then(([h, d, s, sc]) => {
      setHubs(h)
      setDevices(d)
      setSources(s)
      setScenario(sc.scenarios.find((x) => x.scenario_id === sc.active_id) ?? null)
    })
  }, [api])

  const cards = (sources?.sources ?? []).filter((s) => s.kind === 'card' && s.status === 'ready')
  const card: CardChoice | null = uniquify && cardId ? { source_id: cardId, placement, seconds: cardSeconds } : null

  // A card uploaded from here lands in the catalogue like any other source and is selected at once.
  const uploadCard = async (file: File | undefined) => {
    if (!file) return
    setCardBusy(true)
    try {
      const uploaded = await uploadSource(api, 'card', file, () => {})
      if (uploaded.status !== 'ready') throw new Error(uploaded.error ?? t.videos.rejected)
      setSources(await api.sources())
      setCardId(uploaded.source_id)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setCardBusy(false)
    }
  }

  const targets = toTargets(picked)
  const phoneCount = countPhones(picked, devices, video.direction_id)
  const capacity = sources?.capacity ?? 0
  const short = uniquify && sources !== null && phoneCount > capacity

  const go = async () => {
    setBusy(true)
    setError(null)
    try {
      const started = await api.distribute(video.video_id, targets, uniquify, card, publish)
      toast(t.distribute.started(started.hubs.length))
      onClose()
      navigate(`/distributions/${started.distribution_id}`)
    } catch (e) {
      const detail = e instanceof ApiError ? (e.detail as { detail?: { error?: string; needed?: number; available?: number } })?.detail : null
      if (detail?.error === 'not_enough_sources' && detail.needed !== undefined && detail.available !== undefined) {
        setError(t.distribute.notEnoughSources(detail.needed, detail.available))
      } else setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="scrim" role="dialog" aria-modal="true" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog has-scroll-body" style={{ maxWidth: 640 }}>
        <h3>{t.distribute.title(videoName(video))}</h3>
        <p className="dim small">{t.distribute.body}</p>
        <div className="dialog-scroll">

          <TargetPicker hubs={hubs} devices={devices} picked={picked} onChange={setPicked} direction={video.direction_id} />

          <div className="panel">
            <label className="panel-row" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={uniquify} onChange={(e) => setUniquify(e.target.checked)} />
              <b>{t.distribute.uniquify}</b>
              <span className="dim small">{t.distribute.uniquifyHint}</span>
              <span className="grow" />
              {sources && uniquify && (
                <Badge kind={short ? 'bad' : capacity > 0 ? 'ok' : 'warn'}>{t.distribute.capacity(phoneCount, capacity)}</Badge>
              )}
            </label>
            {short && (
              <span className="tiny" style={{ color: 'var(--bad)' }}>
                {t.distribute.notEnoughSources(phoneCount, capacity)}
              </span>
            )}
            {uniquify && (
              <div className="phone-choices" style={{ marginTop: 'var(--sp-2)' }}>
                <div className="row">
                  <b>{t.distribute.card}</b>
                  <span className="dim small">{t.distribute.cardHint}</span>
                </div>
                <div className="row">
                  <select value={cardId} onChange={(e) => setCardId(e.target.value)} disabled={cardBusy}>
                    <option value="">{t.distribute.cardNone}</option>
                    {cards.map((c) => (
                      <option key={c.source_id} value={c.source_id}>
                        {c.filename}
                      </option>
                    ))}
                  </select>
                  <input
                    ref={cardInput}
                    type="file"
                    accept="image/jpeg,image/png,.jpg,.jpeg,.png"
                    hidden
                    onChange={(e) => {
                      void uploadCard(e.target.files?.[0])
                      e.target.value = ''
                    }}
                  />
                  <button className="ghost btn-sm" disabled={cardBusy} onClick={() => cardInput.current?.click()}>
                    {cardBusy ? t.distribute.sending : t.distribute.cardUpload}
                  </button>
                </div>
                {cardId && (
                  <>
                    <div className="row">
                      {(['start', 'end', 'both'] as CardPlacement[]).map((p) => (
                        <label key={p} className="phone-choice is-on" style={{ cursor: 'pointer' }}>
                          <input type="radio" name="card-placement" checked={placement === p} onChange={() => setPlacement(p)} />
                          <span>{t.distribute.cardPlacement[p]}</span>
                        </label>
                      ))}
                      <input
                        type="number"
                        min={1}
                        max={5}
                        step={0.5}
                        value={cardSeconds}
                        onChange={(e) => setCardSeconds(Math.min(5, Math.max(1, Number(e.target.value) || 1)))}
                        style={{ width: '4.5em' }}
                      />
                      <span className="dim small">{t.distribute.cardSeconds}</span>
                    </div>
                    {placement !== 'end' && <span className="tiny" style={{ color: 'var(--warn)' }}>{t.distribute.cardRetention}</span>}
                  </>
                )}
              </div>
            )}
            <span className="dim tiny">{t.distribute.delaysHint}</span>
          </div>

          <div className="panel">
            <label className="panel-row" style={{ cursor: scenario ? 'pointer' : 'not-allowed' }}>
              <input type="checkbox" checked={publish && Boolean(scenario)} disabled={!scenario} onChange={(e) => setPublish(e.target.checked)} />
              <b>{t.distribute.publish}</b>
              <span className={`small${scenario ? ' dim' : ''}`} style={scenario ? undefined : { color: 'var(--warn)' }}>
                {scenario === undefined ? '' : scenario ? t.distribute.publishWith(scenario.name) : t.distribute.publishNone}
              </span>
            </label>
          </div>

        </div>
        {error && (
          <span className="tiny" style={{ color: 'var(--bad)' }}>
            {error}
          </span>
        )}
        <div className="actions">
          <button className="ghost" onClick={onClose} disabled={busy}>
            {t.confirm.cancel}
          </button>
          <button className="primary" disabled={busy || targets.length === 0 || short} onClick={() => void go()}>
            {busy ? t.distribute.sending : t.distribute.send(targets.length)}
          </button>
        </div>
      </div>
    </div>
  )
}

/** What a video is called on screen: its title when a person gave it one, else the filename. */
export function videoName(v: { title?: string | null; filename: string }): string {
  return v.title ?? v.filename
}

/** The title, edited in place: click, type, Enter or blur saves, Escape gives up, empty clears. */
function VideoTitle({ video, onSaved }: { video: Video; onSaved: () => void }) {
  const { api, auth } = useApp()
  const mine = auth.mayTouch(video.uploaded_by)
  const t = useT()
  const current = video.title ?? ''
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(current)

  const save = async () => {
    setEditing(false)
    const next = value.trim()
    if (next === current) return
    try {
      await api.renameVideo(video.video_id, next)
      toast(t.videos.renamed(next || video.filename))
      onSaved()
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), 'bad')
      setValue(current)
    }
  }

  return editing ? (
    <input
      autoFocus
      value={value}
      placeholder={video.filename}
      aria-label={t.videos.titleLabel}
      style={{ width: 260 }}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => void save()}
      onKeyDown={(e) => {
        if (e.key === 'Enter') void save()
        if (e.key === 'Escape') {
          setValue(current)
          setEditing(false)
        }
      }}
    />
  ) : (
    <button
      className="chip video-title"
      title={mine ? t.videos.renameTitle : t.common.notYours}
      disabled={!mine}
      style={{ fontWeight: 600 }}
      onClick={() => {
        setValue(current)
        setEditing(true)
      }}
    >
      {videoName(video)}
    </button>
  )
}

/** One video's posts, newest first: when, which account (on its direction's colour), the numbers, the link. */
function VideoHistory({ videoId }: { videoId: string }) {
  const { api, authEpoch } = useApp()
  const t = useT()
  const dirs = useDirections()
  const got = usePoll(() => api.publications({ video_id: videoId, limit: 200, on_phone: true }), 60000, [api, authEpoch, videoId])
  const rows = got.data?.publications ?? []
  if (!got.data) return <span className="tiny dim">{t.distribute.loading}</span>
  if (rows.length === 0) return <span className="tiny dim">{t.videos.neverPosted}</span>
  return (
    <ul className="video-history">
      {rows.map((p) => (
        <li key={p.publication_id}>
          <span className="tiny mono">{p.published_at ? exactly(p.published_at) : '—'}</span>
          <span className="acc-pill" data-color={p.direction_id ? dirs.byId.get(p.direction_id)?.color : undefined}>
            {p.account_label || p.device_name || p.serial}</span>
          <PhoneLink deviceId={p.device_name ? p.device_id : null} name={p.device_name} serial={p.serial} />
          <span className="tiny">{p.stats
            ? `${(p.stats.views ?? 0).toLocaleString()} ${t.videos.viewsWord} · ${(p.stats.likes ?? 0).toLocaleString()} ${t.videos.likesWord}`
            : t.videos.notReadYet}</span>
          {p.url ? <a className="tiny" href={p.url} target="_blank" rel="noreferrer">{t.videos.openPost}</a>
                 : <span className="tiny warn-text">{t.videos.noLink}</span>}
        </li>
      ))}
    </ul>
  )
}
